import type { NlpFilters } from '@massilia/queries'

import { z } from 'zod'

import type {
  LuggageFilterValue,
  PaymentFilterValue,
  SortFilterValue,
  StopsFilterValue,
} from '@/lib/schemas/flight-results-search.schema'

import type { SegmentTimeFilter, TimeSlot } from './time-filter'

/** Borne numérique lue depuis le sac de search params, non typé à ce stade. */
const boundSchema = z.number()

const TIME_SLOT_VALUES = ['0-6', '6-12', '12-18', '18-24'] as const satisfies readonly TimeSlot[]

// L'URL est éditable à la main : chaque entrée est décodée, celles qui n'ont pas
// d'index de tronçon exploitable sont écartées plutôt que reconstruites à moitié.
const segmentTimeFilterSchema = z.object({
  arrivalTimeSlots: z.array(z.enum(TIME_SLOT_VALUES)).catch([]),
  departureTimeSlots: z.array(z.enum(TIME_SLOT_VALUES)).catch([]),
  segmentIndex: z.number(),
}) satisfies z.ZodType<SegmentTimeFilter, z.ZodTypeDef, unknown>

export interface FlightUrlFilterParams {
  filterAirlines?: string
  filterDurationMax?: number
  filterDurationMin?: number
  filterLuggage?: LuggageFilterValue
  filterPayment?: PaymentFilterValue
  filterPriceMax?: number
  filterPriceMin?: number
  filterStops?: StopsFilterValue
  filterTimeRanges?: string
}

/**
 * Filtres de la sidebar tels qu'ils vivent dans l'URL d'une page résultats.
 * `filterSort` n'existe que sur /vols/resultats, d'où son caractère optionnel.
 */
export interface SidebarFilterParams extends FlightUrlFilterParams {
  filterSort?: SortFilterValue
}

/**
 * Filtres repris tels quels quand le chat relance une recherche : l'utilisateur
 * a choisi ces critères sur la page résultats, ils ne doivent pas disparaître
 * parce que la recherche change.
 */
export const CARRIED_OVER_FILTER_KEYS = [
  'filterAirlines',
  'filterDurationMax',
  'filterDurationMin',
  'filterLuggage',
  'filterPayment',
  'filterPriceMax',
  'filterPriceMin',
  'filterSort',
  'filterStops',
  'filterTimeRanges',
] as const satisfies readonly (keyof SidebarFilterParams)[]

// Patch passed to setFilters() to clear all sidebar filters at once. Centralized
// here so the sidebar "Réinitialiser" button and the empty-state card stay in
// sync — adding a new filter field requires touching only this object.
export const RESET_FILTERS_PATCH: Partial<FlightUrlFilterParams> = {
  filterAirlines: undefined,
  filterDurationMax: undefined,
  filterDurationMin: undefined,
  filterLuggage: undefined,
  filterPayment: undefined,
  filterPriceMax: undefined,
  filterPriceMin: undefined,
  filterStops: undefined,
  filterTimeRanges: undefined,
}

/**
 * Construit un intervalle de filtre à partir des deux bornes de l'URL.
 *
 * Une borne SEULE est une contrainte valide : le chat en pose souvent une seule
 * (« moins de 3000€ », « à partir de 800€ »). Exiger les deux rendait
 * tout plafond venu du chat silencieusement inopérant — le bot annonçait
 * « OK : moins de 3000€ ✅ » et la liste ne bougeait pas. La borne absente
 * s'ouvre (0 / +∞) au lieu d'annuler le filtre.
 *
 * Partagé par /vols/resultats et /vols-multi/resultats : la règle était copiée
 * dans les deux, et c'est cette duplication qui a laissé le bug vivre des deux
 * côtés.
 */
export function boundsToRange(
  min?: number,
  max?: number
): undefined | { max: number; min: number } {
  if (min === undefined && max === undefined) return undefined
  return { max: max ?? Number.POSITIVE_INFINITY, min: min ?? 0 }
}

/**
 * Fusion des filtres émis par le chat dans les search params de la route.
 *
 * `navigate({ search: (prev) => … })` remplace l'objet search ENTIER, et celui-ci
 * ne contient pas que des filtres : il porte `search` (la
 * `StructuredMultiSearchRequest`, c'est-à-dire la recherche elle-même) et
 * `filterSort`. Partir de `{}` en mode `replace` supprimait donc `search`, la
 * requête se désactivait, et TOUT retrait de filtre réussi vidait la page de ses
 * résultats. Le mode `replace` est une remise à zéro des FILTRES, pas de l'URL :
 * il part de `prev` et applique `RESET_FILTERS_PATCH` — le même objet que le
 * bouton « Réinitialiser » de la sidebar, donc plus de divergence possible quand
 * un filtre s'ajoute.
 */
export function buildFilterSearchPatch<TSearch extends FlightUrlFilterParams>(
  prev: TSearch,
  nextParams: FlightUrlFilterParams,
  replace: boolean
): FlightUrlFilterParams & TSearch {
  const base = replace ? { ...prev, ...RESET_FILTERS_PATCH } : prev
  const merged = { ...base, ...nextParams }
  // Les bornes annulées sont accumulées à part : `merged` porte le type de la
  // route appelante, on n'y écrit pas clé par clé.
  const clearedBounds: FlightUrlFilterParams = {}
  // Une borne qui arrive efface sa jumelle SI les deux se contredisent : « moins
  // de 500€ » sur un plancher à 800€ sortirait une liste vide. Une jumelle
  // compatible survit — le chat sait poser les deux bornes (« entre 800 et
  // 3000€ ») et voit le plancher de la sidebar dans son contexte, donc l'effacer
  // à chaque plafond lui ferait annuler en silence une contrainte non évoquée.
  for (const [minKey, maxKey] of [
    ['filterPriceMin', 'filterPriceMax'],
    ['filterDurationMin', 'filterDurationMax'],
  ] as const) {
    const min = boundSchema.safeParse(merged[minKey])
    const max = boundSchema.safeParse(merged[maxKey])
    if (!min.success || !max.success || min.data <= max.data) continue
    // La borne du nouveau jeu fait foi ; c'est l'ancienne qui saute.
    clearedBounds[nextParams[minKey] !== undefined ? maxKey : minKey] = undefined
  }
  return { ...merged, ...clearedBounds }
}

export function nlpFiltersToUrlParams(filters: NlpFilters): FlightUrlFilterParams {
  const out: FlightUrlFilterParams = {}
  // `'all'` DOIT partir dans l'URL : c'est la valeur neutre par laquelle le chat
  // retire un filtre d'escales. La jeter ne produisait aucun paramètre, donc en
  // fusion l'ancien `filterStops=direct` survivait — pendant que le bot
  // confirmait « OK : toutes escales ✅ ». Sans risque : `filterStops` a déjà
  // `.default('all')` dans les deux routes, `applyFilters` a un `case 'all'`
  // inerte, et `urlParamsToNlpFilters` filtre `'all'` en sortie (donc aucun
  // filtre fantôme ne repart vers le contexte du chat).
  if (filters.stops) out.filterStops = filters.stops
  if (filters.luggage) out.filterLuggage = filters.luggage
  if (filters.maxPrice !== undefined && Number.isFinite(filters.maxPrice)) {
    out.filterPriceMax = filters.maxPrice
  }
  if (filters.minPrice !== undefined && Number.isFinite(filters.minPrice)) {
    out.filterPriceMin = filters.minPrice
  }
  if (filters.maxDurationMinutes !== undefined && Number.isFinite(filters.maxDurationMinutes)) {
    out.filterDurationMax = filters.maxDurationMinutes
  }
  if (filters.excludedAirlines && filters.excludedAirlines.length > 0) {
    out.filterAirlines = filters.excludedAirlines.join(',')
  }
  if (filters.timeFilters && filters.timeFilters.length > 0) {
    out.filterTimeRanges = JSON.stringify(filters.timeFilters)
  }
  return out
}

// Normalize the JSON time-filter param coming from the URL. The chat layer may
// emit entries with only departureTimeSlots OR only arrivalTimeSlots (cf.
// NlpSegmentTimeFilter where both are optional); downstream consumers expect
// both arrays to be present, so default the missing one to []. Also tolerates
// malformed JSON and entries without a numeric segmentIndex.
export function parseFilterTimeRanges(raw: string | undefined): SegmentTimeFilter[] {
  if (!raw) return []
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return []
  }
  if (!Array.isArray(parsed)) return []
  return parsed.flatMap((entry): SegmentTimeFilter[] => {
    const filter = segmentTimeFilterSchema.safeParse(entry)
    return filter.success ? [filter.data] : []
  })
}

export function urlParamsToNlpFilters(params: FlightUrlFilterParams): NlpFilters {
  const out: NlpFilters = {}
  if (params.filterStops && params.filterStops !== 'all') out.stops = params.filterStops
  // `'0'` n'est pas un filtre : c'est le défaut de `validateSearch`, et
  // `filter-utils.ts` le lit comme « au moins 0 bagage » — il matche tout. Le
  // laisser passer publiait un filtre fantôme dans `pageContext.currentFilters`
  // à CHAQUE tour, que le modèle recopiait consciencieusement en mode `replace`.
  // Filtré ici et pas dans `validateSearch` : le défaut est lu par la sidebar et
  // par l'URL de partage ; cette fonction est la traduction vers le contexte chat.
  if (params.filterLuggage && params.filterLuggage !== '0') out.luggage = params.filterLuggage
  if (params.filterPriceMax !== undefined) out.maxPrice = params.filterPriceMax
  if (params.filterPriceMin !== undefined) out.minPrice = params.filterPriceMin
  if (params.filterDurationMax !== undefined) {
    out.maxDurationMinutes = params.filterDurationMax
  }
  if (params.filterAirlines) {
    const codes = params.filterAirlines.split(',').filter(Boolean)
    if (codes.length > 0) out.excludedAirlines = codes
  }
  const timeFilters = parseFilterTimeRanges(params.filterTimeRanges)
  if (timeFilters.length > 0) out.timeFilters = timeFilters
  return out
}
